import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Form, Modal, Row, Col, Spinner } from "react-bootstrap";
import Select from "react-select";
import AsyncSelect from 'react-select/async';
import MyApiClient from '../../../../../api-client/MyApiClient';
import { FirmaUpload } from '../../../../components/Consiglio/FirmaUpload';
import { RUOLI_CONSIGLIO, ruoloConsiglio } from '../../../../components/Consiglio/ruoli';
import { usePageTitle } from "../../../../layouts/PageHeaderExtra";

const LISTA_CDA = '/struttura/cda/list';

const Obbligatorio = () => <span className="text-danger ms-1">*</span>;

const iniziali = (nome) =>
	String(nome || '').split(' ').filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase();

export const SchedaConsiglio = () => {

	const navigate = useNavigate();
	const { id } = useParams();
	const inModifica = !!id;

	const [membro, setMembro] = useState(null);
	// Titolo della pagina = nome del membro del consiglio (in modifica); finché non è caricato resta quello generico
	usePageTitle(membro?.name || '');
	const [loadingShowed, setLoadingShowed] = useState(false);
	const [modalShowed, setModalShowed] = useState(false);
	const [modalTitle, setModalTitle] = useState('');
	const [modalText, setModalText] = useState('');

	const [selectedSocio, setSelectedSocio] = useState(null);
	const [ruolo, setRuolo] = useState(null);
	const [mandateStart, setMandateStart] = useState('');
	const [mandateEnd, setMandateEnd] = useState('');
	const [firmaScelta, setFirmaScelta] = useState(null);
	const [firmaDaRimuovere, setFirmaDaRimuovere] = useState(false);
	// «Rimuovi» e «Visualizza player» erano icone sulla riga della lista, ora stanno qui (03/10/2026)
	const [confermaRimozione, setConfermaRimozione] = useState(false);

	const getAuth = () => {
		const tokenDetailsString = sessionStorage.getItem('userDetails');
		if (!tokenDetailsString) return null;
		const tokenDetails = JSON.parse(tokenDetailsString);
		return { token: tokenDetails.idToken, clubId: tokenDetails.localId };
	};

	const mostraErrore = (testo) => {
		setModalTitle("Errore durante l'operazione");
		setModalText(testo);
		setModalShowed(true);
	};

	// In modifica il membro si legge dall'elenco: non esiste un GET per singolo
	// membro e aggiungerlo per una sola schermata non si giustifica.
	const carica = useCallback(async () => {
		if (!inModifica) return;

		const auth = getAuth();
		if (!auth) return;

		const apiClient = new MyApiClient();
		setLoadingShowed(true);
		const risultato = await apiClient.getClubBoard(auth.token, auth.clubId);
		setLoadingShowed(false);

		const trovato = Array.isArray(risultato) ? risultato.find(m => m.membershipId === id) : null;
		if (!trovato) {
			mostraErrore("Membro del consiglio direttivo non trovato.");
			return;
		}

		setMembro(trovato);
		setRuolo(RUOLI_CONSIGLIO.find(r => r.value === trovato.role) || null);
		setMandateStart(trovato.mandateStart || '');
		setMandateEnd(trovato.mandateEnd || '');
	}, [id, inModifica]);

	useEffect(() => {
		carica();
	}, [carica]);

	// Solo i soci possono entrare nel consiglio: la ricerca attinge al libro soci,
	// non all'anagrafica player.
	const loadSociOptions = async (inputValue) => {
		if (inputValue.length < 3) return [];

		const auth = getAuth();
		if (!auth) return [];

		const apiClient = new MyApiClient();
		const risultato = await apiClient.getMemberBook(auth.token, {
			offset: 0,
			limit: 50,
			search: inputValue,
			bookStatus: 'ACTIVE'
		});
		if (!risultato || risultato.error) return [];

		return (risultato.items || []).map(voce => ({ value: voce.userId, label: voce.name }));
	};

	const salvaFirma = async (membershipId) => {
		const auth = getAuth();
		if (!auth) return null;

		const apiClient = new MyApiClient();
		if (firmaScelta) return await apiClient.updateBoardMemberSignature(auth.token, auth.clubId, membershipId, firmaScelta);
		if (firmaDaRimuovere) return await apiClient.deleteBoardMemberSignature(auth.token, auth.clubId, membershipId);
		return null;
	};

	const salva = async () => {
		if (!inModifica && !selectedSocio) {
			mostraErrore("Seleziona il socio da inserire nel consiglio direttivo.");
			return;
		}
		if (!ruolo) {
			mostraErrore("Seleziona il ruolo.");
			return;
		}
		if (!mandateStart || !mandateEnd) {
			mostraErrore("Le date di inizio e fine mandato devono essere valorizzate.");
			return;
		}
		if (mandateStart > mandateEnd) {
			mostraErrore("La data di inizio mandato non può essere successiva a quella di fine.");
			return;
		}

		const auth = getAuth();
		if (!auth) return;

		const apiClient = new MyApiClient();
		setLoadingShowed(true);

		const risultato = inModifica
			? await apiClient.updateBoardMember(auth.token, auth.clubId, id, {
				role: ruolo.value,
				mandateStart,
				mandateEnd
			})
			: await apiClient.addBoardMember(auth.token, auth.clubId, {
				userId: selectedSocio.value,
				role: ruolo.value,
				mandateStart,
				mandateEnd
			});

		if (!risultato || risultato.error) {
			setLoadingShowed(false);
			mostraErrore("Si è verificato un errore durante il salvataggio: " + ((risultato && risultato.error) || 'errore generico'));
			return;
		}

		// La firma ha endpoint suoi: si salva dopo, quando il membro esiste ed ha
		// un membershipId a cui agganciarla.
		const esitoFirma = await salvaFirma(risultato.membershipId || id);
		setLoadingShowed(false);

		if (esitoFirma && esitoFirma.error) {
			mostraErrore("Il membro è stato salvato, ma la firma non è stata caricata: " + esitoFirma.error);
			return;
		}
		navigate(LISTA_CDA);
	};

	const rimuovi = async () => {
		setConfermaRimozione(false);
		const auth = getAuth();
		if (!auth) return;
		const apiClient = new MyApiClient();
		setLoadingShowed(true);
		const risultato = await apiClient.removeBoardMember(auth.token, auth.clubId, id);
		setLoadingShowed(false);
		if (risultato && risultato.error) {
			mostraErrore("Non è stato possibile rimuovere il membro: " + risultato.error);
			return;
		}
		navigate(LISTA_CDA);
	};

	const ruoloCorrente = membro ? ruoloConsiglio(membro.role) : null;

	return (
		<>
			<Modal className="fade" show={loadingShowed} centered>
				<Modal.Body className="text-center">
					<Spinner animation="border" role="status">
						<span className="sr-only">Loading...</span>
					</Spinner>
					<div>Caricamento in corso...</div>
				</Modal.Body>
			</Modal>

			<Modal className="fade" show={modalShowed} centered>
				<Modal.Header>
					<Modal.Title>{modalTitle}</Modal.Title>
					<Button onClick={() => setModalShowed(false)} variant="" className="btn-close"></Button>
				</Modal.Header>
				<Modal.Body>
					<Row><Col>{modalText}</Col></Row>
				</Modal.Body>
				<Modal.Footer className="justify-content-between">
					<Button className="btn btn-primary btn-add" onClick={() => setModalShowed(false)}>Chiudi</Button>
				</Modal.Footer>
			</Modal>

			<Modal className="fade" show={confermaRimozione} onHide={() => setConfermaRimozione(false)} centered>
				<Modal.Header closeButton><Modal.Title>Rimuovere dal consiglio?</Modal.Title></Modal.Header>
				<Modal.Body>
					Rimuovere «{membro?.name}» dal consiglio direttivo? Resta socio della struttura: viene eliminata solo la carica.
				</Modal.Body>
				<Modal.Footer className="justify-content-between">
					<Button className="btn btn-light btn-add" onClick={() => setConfermaRimozione(false)}>Annulla</Button>
					<Button className="btn btn-danger btn-add" onClick={rimuovi}>Rimuovi</Button>
				</Modal.Footer>
			</Modal>

			<div style={{ width: '50%', minWidth: '440px' }}>
				<div className="card">
					<div className="card-header">
						<h4 className="card-title">Socio di riferimento</h4>
					</div>
					<div className="card-body">
						{inModifica ? (
							<>
								<div className="d-flex align-items-center gap-2 mb-3">
									<span className="sid-cd-avatar">
										{membro && membro.picture && membro.picture.uri
											? <img src={membro.picture.uri} alt={membro.name} />
											: iniziali(membro?.name)}
									</span>
									<div>
										<div style={{ fontWeight: 600 }}>{membro?.name || '—'}</div>
										<div className="text-muted" style={{ fontSize: '12px' }}>Socio della Struttura</div>
									</div>
								</div>
								<div className="text-muted" style={{ fontSize: '13px' }}>
									Il socio di riferimento non è modificabile. Per cambiarlo, rimuovi il membro e creane uno nuovo.
								</div>
							</>
						) : (
							<>
								<Form.Label>Cerca socio<Obbligatorio /></Form.Label>
								<AsyncSelect
									value={selectedSocio}
									onChange={setSelectedSocio}
									loadOptions={loadSociOptions}
									placeholder="Inizia a digitare per cercare un socio (minimo 3 caratteri)…"
									noOptionsMessage={({ inputValue }) =>
										inputValue.length < 3
											? "Digita almeno 3 caratteri per iniziare la ricerca"
											: "Nessun socio trovato"
									}
								/>
								<div className="text-muted mt-2" style={{ fontSize: '13px' }}>
									Solo i player iscritti al Libro Soci possono far parte del consiglio direttivo.
								</div>
							</>
						)}
					</div>
				</div>

				<div className="card">
					<div className="card-header">
						<h4 className="card-title">Dati del mandato</h4>
					</div>
					<div className="card-body">
						<div className="mb-3">
							<Form.Label>Ruolo<Obbligatorio /></Form.Label>
							<Select
								value={ruolo}
								onChange={setRuolo}
								options={RUOLI_CONSIGLIO}
								placeholder="Seleziona ruolo…"
							/>
							{membro && !RUOLI_CONSIGLIO.some(r => r.value === membro.role) && (
								<div className="text-muted mt-2" style={{ fontSize: '13px' }}>
									Ruolo attuale: <b>{ruoloCorrente?.label}</b>, non più assegnabile. Salvando ne verrà impostato uno nuovo.
								</div>
							)}
						</div>

						<Row>
							<Col md={6} className="mb-3">
								<Form.Label>Data inizio mandato<Obbligatorio /></Form.Label>
								<Form.Control type="date" value={mandateStart} onChange={(e) => setMandateStart(e.target.value)} />
							</Col>
							<Col md={6} className="mb-3">
								<Form.Label>Data fine mandato<Obbligatorio /></Form.Label>
								<Form.Control type="date" value={mandateEnd} onChange={(e) => setMandateEnd(e.target.value)} />
							</Col>
						</Row>

						<h4 className="card-title mt-4">Firma digitale</h4>
						<p className="text-muted" style={{ fontSize: '13px' }}>
							Carica un file grafico con la firma del membro. Verrà utilizzata nei verbali e nei
							documenti ufficiali del consiglio.
						</p>
						<FirmaUpload
							firmaCorrente={membro?.signature}
							onSelezione={(firma) => { setFirmaScelta(firma); setFirmaDaRimuovere(false); }}
							onRimuovi={() => { setFirmaScelta(null); setFirmaDaRimuovere(true); }}
							onErrore={mostraErrore}
						/>
					</div>
				</div>

				<div className="d-flex justify-content-between gap-2">
					{inModifica && membro
						? <Button className="btn btn-add sid-btn-secondary text-danger" onClick={() => setConfermaRimozione(true)}>Rimuovi dal consiglio</Button>
						: <span />}
					<div className="d-flex gap-2">
					{inModifica && membro && (
						<Button className="btn btn-add sid-btn-secondary" onClick={() => navigate('/struttura/clienti/player_profile/' + membro.userId)}>Visualizza player</Button>
					)}
					<Button className="btn btn-add sid-btn-secondary" onClick={() => navigate(LISTA_CDA)}>Annulla</Button>
					<Button className="btn btn-primary btn-add" style={{ textTransform: 'none' }} onClick={salva}>Salva scheda</Button>
					</div>
				</div>
			</div>
		</>
	);
};

export default SchedaConsiglio;
